import { useAlgoliaInsightsClient } from '@algolia-search/composables/useAlgoliaInsightsClient';
import { useAlgoliaSearch } from '@algolia-search/composables/useAlgoliaSearch';
import { INDEX_PRODUCTS } from '@algolia-search/consts';
import { getProductPrice } from '@algolia-search/helpers/product';
import type { AlgoliaProduct } from '@algolia-search/types';
import { computed, onMounted, useContext } from '@nuxtjs/composition-api';
import type { InsightsEvent, InsightsEventObjectData, InsightsEventType } from 'search-insights';

import type { CurrencyEnum } from '~/modules/GraphQL/types';

export const useAlgoliaInsights = () => {
  const { insightsClient, initialize: initializeInsightsClient } = useAlgoliaInsightsClient();
  const { getIndex } = useAlgoliaSearch();
  const productIndexName = getIndex(INDEX_PRODUCTS);
  const { i18n } = useContext();
  const localeCurrency = computed<CurrencyEnum>(() => i18n.localeProperties.defaultCurrency || 'EUR');

  const sendEvent = (method: string, eventType: InsightsEventType, payload: Omit<InsightsEvent, 'eventType'>) => {
    const event = {
      eventType,
      ...payload
    } as InsightsEvent;

    (insightsClient.value as (method: string, event: InsightsEvent) => void)(method, event);
  };

  const sendClickEvent = (index: string, objectIDs: string[], positions: number[], queryID: string, eventName = 'Hit Clicked') => {
    sendEvent('clickedObjectIDsAfterSearch', 'click', {
      eventName,
      index,
      objectIDs,
      positions,
      queryID
    });
  };

  const sendAddToCartEvent = (eventName: string, index: string, objectIDs: string[], queryID: string) => {
    sendEvent('addedToCartObjectIDsAfterSearch', 'conversion', {
      eventName,
      index,
      objectIDs,
      queryID,
      eventSubtype: 'addToCart'
    });
  };

  const sendPurchaseEvent = (products: (AlgoliaProduct & { quantity: number })[], orderValue?: number) => {
    const objectIDs: string[] = products.map((hit) => hit.objectID).filter(Boolean);

    const objectData: InsightsEventObjectData[] = products.map((hit) => {
      const data: InsightsEventObjectData = {
        quantity: hit.quantity
      };

      if (hit.__queryID) {
        data.queryID = hit.__queryID;
      }

      if (hit.price) {
        data.price = getProductPrice(hit, localeCurrency.value).value;
      }

      return data;
    });

    const payload: Omit<InsightsEvent, 'eventType'> = {
      eventName: 'Product Purchased',
      index: productIndexName,
      objectIDs,
      objectData,
      currency: localeCurrency.value,
      ...(orderValue && { value: orderValue })
    };

    sendEvent('purchasedObjectIDsAfterSearch', 'conversion', payload);
  };

  onMounted(async () => {
    await initializeInsightsClient();
  });

  return {
    sendClickEvent,
    sendAddToCartEvent,
    sendPurchaseEvent
  };
};
